import { Alert, Autocomplete, Chip, FormControlLabel, MenuItem, Switch, TextField } from "@mui/material";
import { useState } from "react";
import { CatalogService } from "@services/catalog/catalogService";
import { BASE_KINDS, CatalogMetricType } from "@models/catalog.model";
import { FormDialog } from "./FormDialog";

export function MetricTypeDialog({
  verticalId, initial, onClose, onSaved,
}: {
  verticalId: number;
  initial: CatalogMetricType | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const svc = new CatalogService();
  const [code, setCode] = useState(initial?.code ?? "");
  const [name, setName] = useState(initial?.name ?? "");
  const [baseKind, setBaseKind] = useState(initial?.base_kind ?? "string");
  const [allowedValues, setAllowedValues] = useState<string[]>(initial?.allowed_values ?? []);
  const [validator, setValidator] = useState(initial?.validator ?? "");
  // New types default to vertical-specific (domain types); existing global types stay global.
  const [scoped, setScoped] = useState(initial ? initial.vertical_id != null : true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A domain type with no value-set and no validator is functionally identical to the built-in
  // base primitive of the same kind — that's how junk types like a bare "age (int)" get created.
  // Warn (don't block — a name-only alias is occasionally intentional). Never warn on the base
  // primitives themselves (existing global types).
  const isBaseType = initial != null && initial.vertical_id == null;
  const duplicatesBase = !isBaseType && allowedValues.length === 0 && !validator.trim();

  const save = async () => {
    setSaving(true);
    setError(null);
    const payload = {
      code, name, base_kind: baseKind,
      vertical_id: scoped ? verticalId : null,
      allowed_values: allowedValues, validator,
    };
    try {
      if (initial) await svc.updateMetricType(initial.id, payload);
      else await svc.createMetricType(payload);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormDialog
      open
      title={initial ? "Edit metric type" : "New metric type"}
      onClose={onClose}
      onSave={save}
      saving={saving}
      error={error}
      canSave={!!code.trim() && !!name.trim()}
    >
      {duplicatesBase && (
        <Alert severity="warning">
          A “{baseKind}” type with no allowed values or validator just duplicates the built-in{" "}
          {baseKind} type. Add allowed values or a validator to make it a real domain type — or
          cancel and pick the base {baseKind} type on your metric instead.
        </Alert>
      )}
      <TextField label="Code" value={code} onChange={(e) => setCode(e.target.value)}
        helperText="Globally unique, e.g. retail_tier" size="small" autoFocus />
      <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} size="small" />
      <TextField select label="Base kind" value={baseKind}
        onChange={(e) => setBaseKind(e.target.value)}
        helperText="The primitive the cleaner coerces to" size="small">
        {BASE_KINDS.map((k) => <MenuItem key={k} value={k}>{k}</MenuItem>)}
      </TextField>
      <Autocomplete
        multiple freeSolo options={[]} value={allowedValues}
        onChange={(_, v) => setAllowedValues(v as string[])}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => <Chip size="small" label={option} {...getTagProps({ index })} key={option} />)
        }
        renderInput={(params) => (
          <TextField {...params} label="Allowed values" size="small"
            helperText="Enum value-set (type Enter to add). Leave empty for free-form." />
        )}
      />
      <TextField label="Validator" value={validator} onChange={(e) => setValidator(e.target.value)}
        helperText='Optional named validator / regex, e.g. gpa_0_4 (display-only for now)' size="small" />
      <FormControlLabel
        control={<Switch checked={scoped} onChange={(e) => setScoped(e.target.checked)} />}
        label="Specific to this vertical (off = global base type)"
      />
    </FormDialog>
  );
}
